import React, { FC, useMemo } from "react";

import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import clsx from "clsx";

import { SizeGuideTable } from "~/bff/types/SizeGuideTable";
import { getTestAutomationProps } from "~/helpers/test-automation-props";

import { SIZE_TABLE_CLASSES } from "./constants";
import { SizeTableContainer } from "./styled";

export interface SizeGuideTablePropsExtended extends SizeGuideTable {
  className?: string;
  tableTitle?: string;
}

const SizeTableComponent: FC<SizeGuideTablePropsExtended> = ({
  className,
  columns,
  tableTitle,
}) => {
  const [firstColumn] = columns ?? [];

  const isTableWithRowTitles = useMemo<boolean>(
    () =>
      firstColumn?.cells
        ? firstColumn.cells.reduce<boolean>(
            (acc, cell) => acc && !!cell?.title,
            true,
          )
        : false,
    [firstColumn],
  );

  const rowTitlesOrder = useMemo(
    () =>
      firstColumn?.cells ? firstColumn?.cells?.map((cell) => cell?.title) : null,
    [firstColumn],
  );

  return (
    /* NOSONAR */
    <SizeTableContainer
      className={clsx(className)}
      role="textbox"
      aria-readonly="true"
      {...getTestAutomationProps("table")}
    >
      <Table className={SIZE_TABLE_CLASSES.table} aria-label={tableTitle}>
        <TableHead>
          <TableRow>
            {isTableWithRowTitles && (
              <TableCell
                align="left"
                className={clsx(
                  SIZE_TABLE_CLASSES.head,
                  SIZE_TABLE_CLASSES.firstCell,
                )}
              />
            )}
            {columns?.map((column, index) => (
              <TableCell
                key={index}
                align="left"
                className={SIZE_TABLE_CLASSES.head}
                {...getTestAutomationProps("column-title-cell")}
              >
                <Box component="strong">{column?.columnTitle}</Box>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rowTitlesOrder?.map((rowTitle, index) => (
            <TableRow key={index}>
              {isTableWithRowTitles ? (
                <>
                  <TableCell
                    align="left"
                    className={SIZE_TABLE_CLASSES.firstColumn}
                    scope="row"
                    variant="head"
                    component="th"
                    {...getTestAutomationProps("first-column-cell")}
                  >
                    <Box component="strong">{rowTitle}</Box>
                  </TableCell>
                  {columns?.map((column, i) => {
                    const [cellWithTitle] = column?.cells
                      ? column.cells.filter(
                          (cell) => cell?.title === rowTitlesOrder[index],
                        )
                      : [null];
                    return (
                      <TableCell
                        key={i}
                        align="left"
                        className={SIZE_TABLE_CLASSES.cell}
                        {...getTestAutomationProps("column-cell")}
                      >
                        {cellWithTitle ? cellWithTitle.value : null}
                      </TableCell>
                    );
                  })}
                </>
              ) : (
                <>
                  {columns?.map((column, i) => (
                    <TableCell
                      key={i}
                      align="left"
                      className={SIZE_TABLE_CLASSES.cell}
                      {...getTestAutomationProps("column-cell")}
                    >
                      {column?.cells?.[index]?.value}
                    </TableCell>
                  ))}
                </>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </SizeTableContainer>
  );
};

export const SizeTable = React.memo<SizeGuideTablePropsExtended>(SizeTableComponent);
